import { badgeById, type FinishRunResponse } from '@accra/shared';
import { el } from '../components/el';

interface ShareOutcome {
  result: 'copied' | 'failed' | 'unavailable';
  url?: string;
}

/** Under the shift summary: did you beat your friend, new badges, daily-challenge rank, and the share button. */
export class OverExtras {
  readonly root = el('div', 'over-extras');
  private readonly line = el('p', 'over-line');
  private readonly badges = el('div', 'over-badges');
  private readonly share = el('button', 'btn share-btn', '📤 Share Link');
  private readonly shareNote = el('small', 'name-message');
  private readonly card = el('button', 'btn card-btn', '🖼 Share score card');
  private readonly link = el('input', 'share-link');

  constructor(onShare: () => Promise<ShareOutcome>, onCard: () => Promise<'shared' | 'saved' | 'failed'>) {
    this.share.hidden = true;
    this.share.addEventListener('click', () => {
      this.share.disabled = true;
      this.shareNote.textContent = 'Making your link…';
      void onShare().then(({ result, url }) => {
        this.share.disabled = false;
        if (result === 'unavailable') {
          this.shareNote.textContent = 'Connect to the internet to challenge a friend.';
          return;
        }
        this.link.value = url ?? '';
        // this.link.hidden = !url;
        if (result === 'copied') {
          this.share.textContent = '✓ Link copied!';
          this.shareNote.textContent = 'Paste it in WhatsApp or a message to your friend.';
        } else {
          this.shareNote.textContent = 'Could not copy automatically. Copy this link:';
          this.link.focus();
          this.link.select();
        }
      });
    });
    this.link.readOnly = true;
    this.link.hidden = true;
    this.link.addEventListener('focus', () => this.link.select());
    this.card.addEventListener('click', () => {
      this.card.disabled = true;
      this.card.textContent = 'Making your card…';
      void onCard().then((result) => {
        this.card.disabled = false;
        this.card.textContent = result === 'failed' ? '🖼 Try the card again' : result === 'saved' ? '✓ Card saved to your downloads' : '✓ Card shared';
        window.setTimeout(() => (this.card.textContent = '🖼 Share score card'), 4000);
      });
    });
    const buttons = el('div', 'share-buttons');
    buttons.append(this.share);
    // buttons.append(this.share, this.card);
    this.root.append(this.line, this.badges, buttons, this.shareNote, this.link);
  }

  /** A new summary: the button is there straight away, waiting for the server to save the shift. */
  reset(): void {
    this.line.textContent = '';
    this.line.className = 'over-line';
    this.badges.replaceChildren();
    this.share.hidden = false;
    this.share.disabled = true;
    this.share.textContent = '📤 Share Link';
    this.shareNote.textContent = 'Saving your shift… then you can send a link.';
    this.link.hidden = true;
  }

  /** No connection: the shift is queued, and a link can only be made once it has been saved. */
  offline(): void {
    this.shareNote.textContent = 'Offline: connect to the internet to challenge a friend.';
  }

  /** Called when the server has accepted the shift. */
  confirm(result: FinishRunResponse, kind: 'normal' | 'daily' | 'friend'): void {
    if (result.friend) {
      this.line.textContent = result.friend.beaten ? `🏆 You beat ${result.friend.name}'s ${result.friend.score.toLocaleString()}!` : `${result.friend.name} still leads with ${result.friend.score.toLocaleString()}. Try again!`;
      this.line.classList.add(result.friend.beaten ? 'won' : 'lost');
    } else if (kind === 'daily' && result.ranks.challenge) {
      this.line.textContent = `📅 Daily challenge: #${result.ranks.challenge} today`;
      this.line.classList.add('won');
    }
    this.badges.replaceChildren(
      ...result.newBadges.flatMap((id) => {
        const badge = badgeById(id);
        if (!badge) return [];
        const card = el('div', 'new-badge');
        card.append(el('span', 'new-badge-icon', badge.icon), el('div', '', ''));
        const text = card.lastElementChild;
        text?.append(el('b', '', `New badge: ${badge.name}`), el('small', '', badge.blurb));
        return [card];
      }),
    );
    this.share.hidden = !result.accepted;
    this.share.disabled = !result.accepted;
    this.shareNote.textContent = result.accepted ? 'Tap to copy a link. Your friend plays your exact road and tries to beat you.' : '';
    if (kind === 'daily') this.share.textContent = '📤 Challenge a friend to beat this';
  }
}
